import { AlertTriangleIcon, Flag01Icon, SwitchHorizontal02Icon } from '@systemeio/icons'
import FunnelStepActions from 'modules/funnels/funnel/components/funnel-actions'
import {
  NavigationCard,
  type NavigationCardProps,
} from 'modules/funnels/funnel/components/navigation-card'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import { getFunnelStepTypeIcon } from 'modules/funnels/funnel/utils/get-funnel-step-type-icon'
import { isFunnelStepSplitTestVariant } from 'modules/funnels/funnel/utils/split-tests-utils'
import React, { forwardRef } from 'react'
import { Tooltip } from 'shared/components'
import { FunnelStepTypeTitlesEnum } from 'shared/enums/funnel-step-type-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'

interface FunnelStepNavigationCardProps extends Omit<
  NavigationCardProps,
  'icon' | 'subtitle' | 'subtitleIcon' | 'title'
> {
  funnelStep?: FunnelStepInterface
  selected?: boolean
  selectFunnelStep?: (funnelStep: FunnelStepInterface) => void
}

const FunnelStepNavigationCard = forwardRef<HTMLButtonElement, FunnelStepNavigationCardProps>(
  ({ className, funnelStep, inert, selected, selectFunnelStep, ...props }, ref) => {
    const { t } = useLocoTranslation()

    if (!funnelStep) {
      return null
    }

    const isSplitTestVariant = isFunnelStepSplitTestVariant(funnelStep)

    return (
      <div className="group/card relative" inert={inert}>
        <NavigationCard
          {...props}
          ref={ref}
          className={twMerge('pr-12', className)}
          selected={selected}
          title={funnelStep.name}
          subtitle={t(FunnelStepTypeTitlesEnum[funnelStep.type])}
          subtitleIcon={
            funnelStep.goalFunnelStepOfAnySplitTest ? (
              <Tooltip
                triggerProps={{ className: 'group/tooltip' }}
                label={t('dashboard.funnel.split_tests.goal_page_tooltip')}
                mode="hover"
              >
                <Flag01Icon className="group-data-popup-open/tooltip:text-icon-accent transition-base" />
              </Tooltip>
            ) : isSplitTestVariant ? (
              <Tooltip
                triggerProps={{ className: 'group/tooltip' }}
                label={
                  funnelStep.inRunningSplitTest
                    ? t('dashboard.funnel.split_tests.running_tooltip')
                    : t('dashboard.funnel.split_tests.selected_tooltip')
                }
                mode="hover"
              >
                <SwitchHorizontal02Icon className="group-data-popup-open/tooltip:text-icon-accent transition-base" />
              </Tooltip>
            ) : !funnelStep.page ? (
              <Tooltip
                triggerProps={{ className: 'group/tooltip' }}
                label={t('dashboard.funnel.steps.no_template')}
                mode="hover"
              >
                <AlertTriangleIcon className="group-data-popup-open/tooltip:text-icon-accent transition-base" />
              </Tooltip>
            ) : undefined
          }
          icon={getFunnelStepTypeIcon(funnelStep.type)}
        />
        {!inert && (
          <div className="absolute top-3 right-2">
            <FunnelStepActions funnelStep={funnelStep} selectFunnelStep={selectFunnelStep} />
          </div>
        )}
      </div>
    )
  },
)

FunnelStepNavigationCard.displayName = 'FunnelStepNavigationCard'

export { FunnelStepNavigationCard }
